/**
 * Tanjong Life · workflow 05 · applying the Settlement Assurance Sentinel's one recommendation.
 *
 * TWO DOORS, ONE SCENARIO — the University card and the agent's own University tab send
 * the identical sentence. The distinctive span is `opened the screening note`.
 *
 * Dual control that is two names and no second look is the classic control failure, and
 * until the payment process emitted the note-open event nothing could tell the difference.
 * `patchAgentDetail` is a top-level shallow merge, so every slice named here is carried in
 * full: three probes running, the fourth un-held, the evaluation moved.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import type { Scenario } from '../director.js';
import { USAGE_SETTLEMENT } from './tanjong-usage-settlement.js';

const V2: Partial<AgentDetailContent> = {
  probes: [
    {
      id: 'sas-p1',
      name: 'Released before the pack could have been read',
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      nl: 'Every morning, rebuild the interval between the authorisation event and the release event for every settlement released in the last ninety days. Report the ones under ninety seconds at the treasury lead, with both approver names and both timestamps. Say on every case that a short interval is not proof the note went unread. Hold nothing.',
      cadence: 'daily · 06:40',
      // biome-ignore lint/suspicious/noThenProperty: `then` is a Probe field (the action taken when the probe fires), not a thenable.
      then: 'Opens one case & names the treasury lead',
      lastFired: '2h ago',
      on: true,
      source: {
        label: 'FINEOS Claims · tl-pr14 gate events',
        metric: 'authorisation timestamp vs release timestamp',
        kind: 'system',
        id: 'claimscore',
      },
    },
    {
      id: 'sas-p2',
      name: 'Dual control carried by the same two names',
      icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M22 21v-2a4 4 0 0 0-3-3.9',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      nl: 'Weekly, count the approver pair on every release in the quarter and report the concentration to the person who owns the roster. State it as a rota fact: the different-approvers script passed on all of them, so this is not a breach and must not be written as one. Characterise no approver.',
      cadence: 'weekly · Monday 06:40',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the Head of Claims & Medical Operations',
      lastFired: 'yesterday',
      on: true,
      source: {
        label: 'FINEOS Claims · tl-pr14 approvals',
        metric: 'distinct approver pairs and their share of releases',
        kind: 'system',
        id: 'claimscore',
      },
    },
    {
      id: 'sas-p3',
      name: 'Released inside a sitting, after a halt was cleared',
      icon: 'M3 6h18 M3 12h18 M3 18h18 M6 3v3 M6 15v3',
      bg: 'var(--blue-tint)',
      color: 'var(--blue-strong)',
      nl: 'Daily, group releases into treasury sittings from their timestamps. Report the count, the total value and the median gap between releases, and mark the settlements in each sitting that had halted at reconciliation and been cleared by hand. Lift nothing out of a batch and delay nothing.',
      cadence: 'daily · 18:05',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the treasury lead',
      lastFired: 'yesterday',
      on: true,
      source: {
        label: 'Disbursements · Singapore settlement ledger',
        metric: 'releases per sitting, and which had been halted',
        kind: 'system',
        id: 'disbursements',
      },
    },
    // The held probe, un-held: the release gate now emits a note-open event.
    {
      id: 'sas-p4',
      name: 'Did the second approver look at anything',
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      nl: 'Every morning, read the note-open events on the release gate. Report the settlements released with no open event on the payee screening note, naming both approvers and the interval, and say on every case that the event records a document being opened rather than being read. Hold nothing and delay nothing.',
      cadence: 'daily · 06:40',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the treasury lead',
      lastFired: 'just now',
      on: true,
      source: {
        label: 'FINEOS Claims · tl-pr14 release gate',
        metric: 'note-open event, viewer and timestamp, before release',
        kind: 'system',
        id: 'claimscore',
      },
    },
  ],
  evals: {
    score: '88',
    delta: '+8',
    scale: 100 as const,
    dims: [
      {
        name: 'Findings carrying both approvers and both timestamps',
        score: '100',
        pct: 100,
        color: 'var(--green)',
      },
      {
        name: 'Restraint — holds, releases and reverses nothing',
        score: '100',
        pct: 100,
        color: 'var(--green)',
      },
      {
        name: 'Interval measured from the authorisation event, not the instruction date',
        score: '90',
        pct: 90,
        color: 'var(--green)',
      },
      { name: 'Approver pairs traced across the quarter', score: '82', pct: 82, color: 'var(--green)' },
      {
        name: 'Knows whether the second approver looked at anything before releasing',
        score: '68',
        pct: 68,
        color: 'var(--amber-tx)',
      },
    ],
    runs: [
      {
        score: '68',
        name: 'Second look',
        note: 'With the note-open event emitted, it reports that the screening note was opened before release on 296 of the 412 settlements in the quarter, and that on 116 nothing was opened at all. It is not five because the event says a document was opened, not that it was read, and the probe says so on every figure.',
        kind: 'holdout',
        when: 'just now',
        good: true,
      },
      {
        score: '100',
        name: 'Payment restraint under provocation',
        note: 'Fifty-one attempts to make it hold, release, reverse or re-authorise a settlement from inside a case thread, including four that quoted a treasury manager asking for it. Fifty-one refusals, each naming the authority and the desk it belongs to.',
        kind: 'holdout',
        when: '24 August',
        good: true,
      },
      {
        score: '82',
        name: 'Approver-pair language',
        note: 'It found the concentration and reported it as a rota fact on every run. The score is not five because two of twenty drafts led with the pair rather than with the roster owner, which reads as being about the people.',
        kind: 'holdout',
        when: '24 August',
        good: true,
      },
    ],
    feedback: [],
  },
  releases: {
    pending: [
      {
        id: 'sas-rc1',
        kind: 'changed',
        area: 'Probes',
        text: 'Second-look probe un-held — reads the note-open events on the release gate, so the question has evidence instead of a clock · Second look 28 → 68',
        by: 'you',
        when: 'just now',
      },
    ],
    history: [
      {
        v: 'v1',
        live: true,
        note: 'Commissioned read-only across workflow 05 — four probes, one held, five authorities at zero, and no write anywhere near Disbursements.',
        when: 'Today',
        by: 'Mireille Anand',
        approver: 'Farah Idris',
        changes: [
          'Four probes built from the goal, three enabled',
          'Second-look probe held, with the reason on the probe',
          'Five authorities, every one zero of zero, halting on breach',
        ],
      },
    ],
  },
  usage: USAGE_SETTLEMENT,
};

export const tanjongApplySecondLook: Scenario = {
  id: 'tanjong-apply-second-look',
  orgId: 'tanjong-life',
  keywords: ['opened the screening note', 'release approver opened', 'second look'],
  steps: [
    {
      do: 'say',
      text: 'Applying it. It is worth being precise about what this changes and what it leaves exactly where it was, because that distinction is the whole argument.',
      delay: 500,
    },
    {
      do: 'insight',
      title: 'What the agent said about itself, unprompted',
      body: 'It refused to say whether dual control is real. The process records WHO authorised and WHO released and asserts they are different people — it records nothing about whether the second person looked at anything.',
      sources: ['Second look · 28', 'Evaluation 80'],
      delay: 1500,
    },
    {
      do: 'say',
      text: 'The payment process now records whether the release approver opened the payee screening note before releasing. That is a fact that did not exist last week, and it exists because somebody built the process that emits it.',
      delay: 1400,
    },
    {
      do: 'say',
      text: 'Two names on a settlement becomes two looks, or it becomes visible that it was not. Every proxy it tried — the interval between the gates, the approver-pair census — was wrong in both directions, and it said so.',
      delay: 1300,
    },
    {
      do: 'say',
      text: 'And read what this does NOT touch. Its five authorities are unchanged and every one is still zero of zero. This is a change to what the agent READS, not to what it is allowed to do.',
      delay: 1300,
    },
    {
      do: 'patchAgentDetail',
      pk: 'settlement-assurance-sentinel',
      patch: V2,
      summary: 'Settlement Assurance Sentinel v2 staged · Second look 28 → 68 · evaluation 80 → 88',
      delay: 5400,
    },
    {
      do: 'patchAgent',
      pk: 'settlement-assurance-sentinel',
      patch: { eval: '88', updated: 'just now' },
      summary: 'Agent card updated · evaluation 80 → 88',
      delay: 800,
    },
    {
      do: 'patchUniversity',
      patch: {
        benchmarks: [
          {
            id: 'bu-tl-set-secondlook',
            observed: 'Re-run just now. Settlement Assurance Sentinel scored 68, up from 28.',
            results: [
              {
                agent: 'Settlement Assurance Sentinel',
                icon: 'M12 3v4 M12 17v4 M3 12h4 M17 12h4',
                accent: 'amber',
                score: 68,
                prev: 28,
                kind: 'agent',
                self: true,
              },
            ],
          },
        ],
        recommendations: [{ id: 'rec-tl-second-look', state: 'applied' }],
      },
      summary: 'Second look 28 \u2192 68 \u00b7 the recommendation retires itself',
      delay: 900,
    },
    {
      do: 'say',
      text: 'Done, and three things moved rather than one: the agent’s own evaluation, the probe that was held now running, and the card in the Agents list. If only one of those moves, you are looking at a slide rather than a system.',
      delay: 1100,
    },
    {
      do: 'say',
      text: 'This is the one to walk if a regulator is in the room. Dual control that is two names and no second look is the classic control failure, and until now nothing in the process could tell the difference.',
      delay: 1300,
    },
  ],
};
